'use client';

/**
 * The invitation, always within reach on a phone.
 *
 * The landing is a long page and the call to action lived only at its two ends:
 * a visitor convinced somewhere in the middle had to hunt for a way in. This bar
 * appears once the hero has scrolled away and hides again at the foot of the
 * page, where the real CTA section takes over — so the two never shout at once.
 *
 * Phone only: on a desktop the header keeps its own button in view.
 */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { Sparkles } from 'lucide-react';

export function StickyCta() {
  const t = useTranslations();
  const locale = useLocale();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const hero = document.querySelector('[data-testid="landing-hero-section"]');
    const end = document.querySelector('[data-testid="landing-cta-section"]');
    if (!hero) return;

    let heroGone = false;
    let atEnd = false;
    const apply = () => setShow(heroGone && !atEnd);

    const heroWatch = new IntersectionObserver(
      ([e]) => {
        heroGone = !e.isIntersecting;
        apply();
      },
      { threshold: 0 },
    );
    heroWatch.observe(hero);

    const endWatch = end
      ? new IntersectionObserver(
          ([e]) => {
            atEnd = e.isIntersecting;
            apply();
          },
          { threshold: 0 },
        )
      : null;
    if (end && endWatch) endWatch.observe(end);

    return () => {
      heroWatch.disconnect();
      endWatch?.disconnect();
    };
  }, []);

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 p-3 backdrop-blur transition-transform md:hidden ${
        show ? 'translate-y-0' : 'translate-y-full'
      }`}
      // Out of the way for screen readers and taps while it is parked off-screen.
      aria-hidden={!show}
      data-testid="landing-sticky-cta"
    >
      <div className="mx-auto flex max-w-md items-center gap-3">
        <p className="min-w-0 flex-1 text-xs leading-snug text-muted-foreground">{t('cta.description')}</p>
        <Link
          href={`/${locale}/create?job=hero`}
          tabIndex={show ? 0 : -1}
          className="flex shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg"
          data-testid="landing-sticky-cta-button"
        >
          <Sparkles className="size-4" aria-hidden />
          {t('cta.getStarted')}
        </Link>
      </div>
    </div>
  );
}
